import React, { useState, useRef } from "react";
import {
  Box,
  IconButton,
  TextField,
  Paper,
  Button,
  ClickAwayListener,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { emojiCategories } from "../../constants/emojiData";

const EmojiPicker = React.memo(({ onEmojiSelect, onClose, theme }) => {
  const [selectedCategory, setSelectedCategory] = useState("smileys");
  const [searchQuery, setSearchQuery] = useState("");
  const emojiPickerRef = useRef(null);

  // get filtered emojis based on search
  const getFilteredEmojis = () => {
    if (!searchQuery) return emojiCategories[selectedCategory];

    const allEmojis = Object.values(emojiCategories).flat();
    return allEmojis.filter(
      (emojiObj) =>
        emojiObj.emoji.includes(searchQuery) || 
        emojiObj.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Paper
        ref={emojiPickerRef}
        sx={{
          position: "fixed",
          bottom: "80px",
          left: "340px",
          width: { xs: "350px", sm: "400px" },
          maxHeight: "300px",
          zIndex: 99999,
          backgroundColor: "#FFFFFF",
          border: "none",
          borderRadius: "12px",
          boxShadow:
            "0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(124, 58, 237, 0.2)",
          overflow: "hidden",
          [theme.breakpoints.down("md")]: {
            left: "50%",
            transform: "translateX(-50%)",
            width: "90vw",
            maxWidth: "400px",
          },
        }}
      >
        {/* search bar */}
        <Box
          sx={{
            p: 2,
            borderBottom: `2px solid #E5E7EB`,
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "#F9FAFB",
          }}
        >
          <TextField
            size="small"
            placeholder="search emoji..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "#6B7280" }} />
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiInputBase-root": {
                fontSize: "0.875rem",
                backgroundColor: "#FFFFFF",
                border: "1px solid #D1D5DB",
                borderRadius: "8px",
                "&:hover": {
                  border: "1px solid #9CA3AF",
                },
                "&.Mui-focused": {
                  border: "2px solid #7C3AED",
                  boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.1)",
                },
              },
            }}
          />
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "#6B7280",
              backgroundColor: "#F3F4F6",
              border: "1px solid #E5E7EB",
              "&:hover": {
                backgroundColor: "#E5E7EB",
                color: "#374151",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* category tabs */}
        <Box
          sx={{
            display: "flex",
            borderBottom: `2px solid #E5E7EB`,
            overflowX: "auto",
            backgroundColor: "#F9FAFB",
          }}
        >
          {Object.keys(emojiCategories).map((category) => (
            <Button
              key={category}
              size="small"
              onClick={() => setSelectedCategory(category)}
              sx={{
                minWidth: "auto",
                px: 3,
                py: 1.5,
                textTransform: "capitalize",
                fontSize: "0.8rem",
                fontWeight: selectedCategory === category ? 600 : 500,
                color:
                  selectedCategory === category ? "#7C3AED" : "#6B7280",
                backgroundColor:
                  selectedCategory === category
                    ? "#F3F4F6"
                    : "transparent",
                borderBottom:
                  selectedCategory === category
                    ? "3px solid #7C3AED"
                    : "none",
                borderRadius: 0,
                "&:hover": {
                  backgroundColor:
                    selectedCategory === category ? "#F3F4F6" : "#F9FAFB",
                  color:
                    selectedCategory === category ? "#7C3AED" : "#374151",
                },
              }}
            >
              {category}
            </Button>
          ))}
        </Box>

        {/* emoji grid */}
        <Box
          sx={{
            p: 2,
            maxHeight: "200px",
            overflowY: "auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(48px, 1fr))",
            gap: 1,
            backgroundColor: "#FFFFFF",
          }}
        >
          {getFilteredEmojis().map((emojiObj, index) => (
            <IconButton
              key={index}
              size="small"
              onClick={() => onEmojiSelect(emojiObj.emoji)}
              sx={{
                fontSize: "2rem",
                width: "48px",
                height: "48px",
                borderRadius: "8px",
                border: "1px solid #F3F4F6",
                backgroundColor: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                  border: "1px solid #7C3AED",
                  transform: "scale(1.05)",
                  transition: "all 0.2s ease",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
              title={emojiObj.name}
            >
              {emojiObj.emoji}
            </IconButton>
          ))}
        </Box>
      </Paper>
    </ClickAwayListener>
  );
});

export default EmojiPicker;
